import { Layout, Skeleton, theme, Grid, message, Spin } from "antd";
import React, { Suspense, useEffect, useMemo, useState } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PullToRefresh from "react-simple-pull-to-refresh";
import HeaderView from "./layout/header";
import Sidebar from "./layout/sidebar";
import { Wrapper } from "./style";
import { TableLoading } from "../components/table-animation";
import { BorderFilter, FormatUserName } from "../config";
import { Admins } from "../routers/admin";
import { Users } from "../routers/user";
import dayjs from "dayjs";
import { MakeApiCall } from "../apis";
import { LoadingOutlined } from "@ant-design/icons";
import { isAuthenticated } from "../utils/auth";
import { useStaffPermissions } from "../hooks/useStaffPermissions";
import { PermissionsSyncProvider } from "../contexts/PermissionsSyncContext";

const { useBreakpoint } = Grid;
const { Content, Footer } = Layout;

const Page = (props) => {
  const { t } = useTranslation();
  const { user, setData } = props;
  const navigate = useNavigate();
  const [reloadPage, setReloadPage] = useState(true);
  const handleRefresh = () => {
    return new Promise((resolve) => {
      console.log("Refreshing...");
      setReloadPage(false);
      setTimeout(() => {
        setReloadPage(true);

        console.log("Refresh complete");
        resolve();
      }, 2000);
    });
  };
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(false);
  const [collapsedButtonClick, setCollapsedButtonClick] = useState(false);
  const [visible, setVisible] = useState(false);
  const staffPermissions = useStaffPermissions();
  const {
    permissions: menuPermissions,
    permissionSignature,
    permissionsReady: permissionsHydrated,
  } = staffPermissions;
  const contentPermissionKey = useMemo(
    () => permissionSignature || user?.token || "default",
    [permissionSignature, user?.token]
  );

  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  const screens = useBreakpoint();

  const alignedLayoutRoutes = ["/dashboard", "/roles", "/user-management"];
  const usesAlignedLayout = alignedLayoutRoutes.includes(location.pathname);
  const usesAlignedHeader =
    usesAlignedLayout ||
    location.pathname.startsWith("/order-reports") ||
    location.pathname.startsWith("/sales-reports") ||
    location.pathname.startsWith("/returns-reports");
  const hideAutoPageHeader =
    location.pathname === "/products/add" ||
    location.pathname === "/products/publish" ||
    location.pathname === "/catalog" ||
    location.pathname === "/manual-upload" ||
    location.pathname === "/app-management" ||
    location.pathname === "/account" ||
    location.pathname === "/callback-sp" ||
    location.pathname === "/callback-ads" ||
    location.pathname === "/callback-salla" ||
    location.pathname === "/callback-zid" ||
    location.pathname === "/noon" ||
    location.pathname === "/trendyol" ||
    location.pathname === "/subscription" ||
    location.pathname.startsWith("/subscription/");

  const handleLogout = () => {
    setData({});
    navigate("/sign-in");
  };
  const handleCollapsed = () => {
    setCollapsedButtonClick(!collapsedButtonClick);
  };

  useEffect(() => {
    if (!collapsedButtonClick) {
      setCollapsed(false);
    } else {
      setCollapsed(!collapsed);
    }
  }, [collapsedButtonClick]);

  // useEffect(() => {
  //   const isSmallScreen = screens.lg === false;
  //   setVisible(false);
  //   setCollapsed(isSmallScreen ? false : collapsedButtonClick);
  // }, []);

  const handleOverlayClick = () => {
    if (screens.lg === false) {
      setVisible(false);
    }
  };

  useEffect(() => {
    if (!isAuthenticated(user)) {
      handleLogout();
    }
    setLoading(false);
    window.addEventListener("storage", (e) => {
      if (e?.storageArea?.length === 0) {
        setData({});
        return navigate("/sign-in");
      }
      if (user) {
        const menusName = (
          !Object.keys(user)?.includes("admin") ? Admins : Users
        )?.map((d) => `/${d?.path}`);

        navigate(
          menusName?.includes(`${location.pathname}`)
            ? window.location.pathname
            : `${menusName?.[0]}`
        );
      }
    });
    return () => {};
  }, []);

  return (
    <PermissionsSyncProvider value={staffPermissions}>
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        overflow: "hidden",
      }}
    >
      <Layout style={{ overflow: "hidden", height: "100%" }}>
        <HeaderView
          collapsed={collapsed}
          user={user}
          location={location}
          setData={setData}
          setCollapsed={setCollapsed}
          handleLogout={handleLogout}
          colorBgContainer={colorBgContainer}
          setVisible={setVisible}
          visible={visible}
          handleCollapsed={handleCollapsed}
          isSmallScreen={screens.lg === false}
        />

        <Layout
          style={{ display: "flex", flexDirection: "row", overflow: "hidden" }}
        >
          {permissionsHydrated ? (
            <Sidebar
              key={permissionSignature || user?.token || "sidebar"}
              user={user}
              menuPermissions={menuPermissions}
              setUser={setData}
              type={user?.loginType}
              collapsed={collapsed}
              location={location}
              setData={setData}
              handleLogout={handleLogout}
              setCollapsed={setCollapsed}
              isSmallScreen={screens.lg === false}
              visible={visible}
              collapsedButtonClick={collapsedButtonClick}
              handleCollapsed={handleCollapsed}
              setVisible={setVisible}
            />
          ) : null}
          {/* Overlay for small screens */}
          {screens.lg === false && visible && (
            <div
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                backgroundColor: "rgba(0, 0, 0, 0.5)",
                zIndex: 1053,
              }}
              onClick={handleOverlayClick}
            ></div>
          )}

          {/* <PullToRefresh onRefresh={handleRefresh}> */}
          <Content
            id="root-scroll"
            style={{
              overflowY: "auto",
              height: "calc(100vh - 120px)",
              marginLeft: "0px",
              paddingTop: "0px",
              borderRadius: "0px",
              marginTop: "0px",
            }}
          >
            {reloadPage ? (
              <Layout>
                {/* Hide header for specific routes */}
                {!hideAutoPageHeader && (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    gap: "4px",
                    // marginBottom: "1rem",
                    // padding: "10px 10px",
                    margin: usesAlignedHeader
                      ? "34px 20px 0px 20px"
                      : "14px 15px 10px 15px",
                  }}
                >
                  <h1
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      color: "#181C32",
                      fontWeight: "700",
                      fontSize: "1.2rem",
                      margin: 0,
                    }}
                  >
                    {t(`sidebar.${location.pathname.split("/")[1].replace(/-([a-z])/g, (g) => g[1].toUpperCase())}`, FormatUserName(location.pathname.split("/")[1]))}
                  </h1>

                  <ul
                    style={{
                      listStyle: "none",
                      padding: 0,
                      margin: "0",
                      display: "flex",
                      alignItems: "center",
                      fontSize: "0.875rem",
                      fontWeight: "600",
                    }}
                  >
                    <li style={{ color: "#A1A5B7" }}>
                      <Link
                        to="/"
                        style={{
                          color: "#A1A5B7",
                          textDecoration: "none",
                          ":hover": { color: "#dc3545" },
                        }}
                      >
                        {t('page.home', 'Home')}
                      </Link>
                    </li>

                    <li
                      style={{
                        margin: "0 0.5rem",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <span
                        style={{
                          backgroundColor: "#B5B5C3",
                          width: "5px",
                          height: "2px",
                          display: "inline-block",
                        }}
                      ></span>
                    </li>

                    <li style={{ color: "#A1A5B7" }}>
                      {t(`sidebar.${location.pathname.split("/")[1].replace(/-([a-z])/g, (g) => g[1].toUpperCase())}`, FormatUserName(location.pathname.split("/")[1]))}
                    </li>
                  </ul>
                </div>
                )}
                <Content
                  style={{
                    margin: usesAlignedLayout
                      ? "20px"
                      : location.pathname === "/subscription" ||
                        location.pathname.startsWith("/subscription/")
                      ? "26px 26px 0px 26px"
                      : location.pathname === '/products/add' ||
                        location.pathname.startsWith('/order-reports')
                      ? "18px 20px 0px 20px"
                      : location.pathname.startsWith('/sales-reports') ||
                        location.pathname.startsWith('/returns-reports') ||
                        location.pathname === '/account' ||
                        location.pathname === '/app-management'
                      ? "20px 20px 0px 20px"
                      : "14px 15px 10px 15px",
                    minHeight: 580,
                    background: "transparent",
                    borderRadius: borderRadiusLG,
                  }}
                >
                  {permissionsHydrated ? (
                    <Suspense
                      fallback={
                        <div
                          className="d-flex justify-content-center align-items-center"
                          style={{ height: "50vh" }}
                        >
                          <Spin
                            indicator={<LoadingOutlined spin />}
                            size="large"
                          />
                        </div>
                      }
                    >
                      <Outlet key={contentPermissionKey} />
                    </Suspense>
                  ) : (
                    <div
                      className="d-flex justify-content-center align-items-center"
                      style={{ height: "50vh" }}
                    >
                      <Spin
                        indicator={<LoadingOutlined spin />}
                        size="large"
                      />
                    </div>
                  )}
                </Content>
                <Footer
                  style={{
                    textAlign: "center",
                    position: "fixed",
                    bottom: "0",
                    width: "85%",
                    height: "38px",
                  }}
                >
                  <div class="text-dark order-2 order-md-1">
                    <span class="fw-semibold me-.0">{t('page.copyright', 'Copyright ©')} </span>
                    <Link
                      to="https://www.loops.sa/"
                      target="_blank"
                      class="text-danger fw-bolder text-gray-900"
                    >
                      {t('page.companyName', 'Loops')}{" "}
                    </Link>
                    {t('page.year', dayjs().format("YYYY"))}
                    {"."}
                    <span class="fw-semibold ms-1">{t('page.allRightsReserved', 'All rights reserved.')}</span>
                    {" · "}
                    <a
                      href="https://www.loops.sa/en/contact-us"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-danger fw-bolder text-gray-900"
                    >
                      {t("page.contactUs", "Contact Us")}
                    </a>
                  </div>
                </Footer>
              </Layout>
            ) : (
              <>
                <div
                  className="card pulse mb-3 mt-4 px-3"
                  style={{ border: BorderFilter }}
                >
                  <div className="card-header d-flex justify-content-between align-items-center">
                    <div className="card-title  d-flex  align-items-center">
                      <Skeleton.Input active />
                    </div>
                    <div className="card-toolbar">
                      <Skeleton.Button active />
                    </div>
                  </div>
                </div>
                <div className="card pulse ">
                  <div
                    style={{
                      padding: "0px 15px",
                      marginTop: "10px",
                    }}
                  >
                    <TableLoading
                      id="test-table"
                      columns={[1, 2, 3, 4, 5]}
                      checkBoxVal={true}
                      actions={[1, 2]}
                    />
                  </div>
                </div>
              </>
            )}
          </Content>
          {/* </PullToRefresh> */}
        </Layout>
      </Layout>
    </div>
    </PermissionsSyncProvider>
  );
};

export default Page;
